/**
 * In-app Roadmap (admin only — gated by AdminRoute)
 * --------------------------------------------------
 * Kanban with:
 *  - drag-to-reorder cards within a column
 *  - drag-to-move cards between columns
 *  - edit (title / description / public toggle / target quarter)
 *  - archive + Archived tab + restore
 *  - rename: "Backlog" → "Considering" (handled server-side in get_board)
 *
 * Drag is powered by @dnd-kit (already in package.json). We use one
 * DndContext at the top with a SortableContext per column. Sensors include
 * an 8px activation distance so single-click on cards still opens edit.
 *
 * Backend endpoints:
 *  GET    /api/roadmap/board?workspace_id=...
 *  GET    /api/roadmap/archived?workspace_id=...
 *  POST   /api/roadmap/cards               (existing — create)
 *  POST   /api/roadmap/cards/reorder       (bulk move/reorder)
 *  POST   /api/roadmap/cards/{id}/restore  (unarchive)
 *  PATCH  /api/roadmap/cards/{id}          (existing — edit + archive via is_archived)
 *  DELETE /api/roadmap/cards/{id}          (existing)
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Globe, Lock, GripVertical, Pencil, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export default function Roadmap() {
  const { currentWorkspace } = useWorkspace();
  const [tab, setTab] = useState("board"); // "board" | "archived"
  const [board, setBoard] = useState({ columns: [], cards: [] });
  const [archived, setArchived] = useState([]);
  const [show, setShow] = useState(false);
  const [draftCol, setDraftCol] = useState("");
  const [editing, setEditing] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    // 8px activation distance — keeps small intentional taps from
    // triggering drag (so the kebab menu / status select are still
    // tappable on mobile).
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/roadmap/board`, { params: { workspace_id: currentWorkspace.workspace_id } });
    setBoard(data);
  };
  const loadArchived = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/roadmap/archived`, { params: { workspace_id: currentWorkspace.workspace_id } });
    setArchived(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);
  useEffect(() => { if (tab === "archived") loadArchived(); /* eslint-disable-next-line */ }, [tab, currentWorkspace]);

  // ─────────────────────────────── DRAG HANDLERS ──────────────────────────────
  // We keep state for the dragged card so the DragOverlay can render it
  // outside its column (no clipping during drag).
  const cardById = useMemo(() => Object.fromEntries(board.cards.map((c) => [c.card_id, c])), [board.cards]);
  const activeCard = activeId ? cardById[activeId] : null;

  const cardsInColumn = (colId) =>
    board.cards
      .filter((c) => c.column_id === colId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const onDragStart = (e) => setActiveId(e.active.id);

  /**
   * dnd-kit gives us `active` (the dragged card) + `over` (the card or column
   * it hovers over). We locally reorder, persist via POST /cards/reorder, and
   * reload on success.
   */
  const onDragEnd = async (e) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const card = cardById[active.id];
    if (!card) return;

    // The over.id can be either another card's id, OR a column droppable
    // ("column-<col_id>") when the user drops into an empty column.
    let targetColId = card.column_id;
    let targetIndex = 0;
    if (String(over.id).startsWith("column-")) {
      targetColId = String(over.id).slice("column-".length);
      targetIndex = cardsInColumn(targetColId).length;
    } else {
      const overCard = cardById[over.id];
      if (!overCard) return;
      targetColId = overCard.column_id;
      const list = cardsInColumn(targetColId);
      targetIndex = list.findIndex((c) => c.card_id === over.id);
      if (targetIndex < 0) targetIndex = list.length;
    }

    // Optimistic local update so the UI feels instant.
    const next = [...board.cards];
    const idx = next.findIndex((c) => c.card_id === card.card_id);
    if (idx >= 0) next.splice(idx, 1);
    const targetList = next
      .filter((c) => c.column_id === targetColId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    targetList.splice(targetIndex, 0, { ...card, column_id: targetColId });
    // Rebuild card list with new positions in the affected column.
    const otherCols = next.filter((c) => c.column_id !== targetColId);
    const rebuiltTarget = targetList.map((c, i) => ({ ...c, position: i }));
    const newCards = [...otherCols, ...rebuiltTarget];
    setBoard({ ...board, cards: newCards });

    // Persist — just the affected column, simplest payload.
    try {
      await api.post(`/roadmap/cards/reorder`, {
        workspace_id: currentWorkspace.workspace_id,
        moves: rebuiltTarget.map((c) => ({
          card_id: c.card_id,
          column_id: targetColId,
          position: c.position,
        })),
      });
    } catch (err) {
      toast.error("Could not save the new order");
      load(); // rollback by re-fetching truth
    }
  };

  // ───────────────────────────── ACTIONS ─────────────────────────────
  const del = async (card) => {
    if (!window.confirm("Delete this card? This cannot be undone.")) return;
    await api.delete(`/roadmap/cards/${card.card_id}`);
    load();
  };
  const archive = async (card) => {
    await api.patch(`/roadmap/cards/${card.card_id}`, { is_archived: true });
    toast.success("Card archived");
    load();
  };
  const restore = async (card) => {
    await api.post(`/roadmap/cards/${card.card_id}/restore`);
    toast.success("Card restored");
    loadArchived();
    load();
  };
  const togglePublic = async (card) => {
    await api.patch(`/roadmap/cards/${card.card_id}`, { is_public: !card.is_public });
    load();
  };

  return (
    <div className="flex flex-col gap-5 sm:gap-6" data-testid="roadmap-page">
      <header className="flex justify-between flex-wrap items-end gap-4">
        <div>
          <p className="bloq-eyebrow">Bloq team · Roadmap</p>
          <h1 className="bloq-headline text-3xl sm:text-4xl lg:text-5xl mt-2">What we're shipping next.</h1>
          <p className="text-sm text-[#82828c] mt-2 max-w-2xl">
            Drag cards to reorder or move between columns. Toggle{" "}
            <Globe className="w-3.5 h-3.5 inline-block -mt-0.5 text-[#005e7a]" /> on a card to
            surface it on the public{" "}
            <a href="/roadmap" target="_blank" rel="noopener noreferrer" className="text-[#005e7a] underline">
              marketing roadmap page
            </a>
            .
          </p>
        </div>
        {tab === "board" && (
          <button
            onClick={() => { setDraftCol(board.columns[0]?.column_id); setShow(true); }}
            className="bloq-btn-primary inline-flex items-center gap-2"
            data-testid="new-card-button"
          >
            <Plus className="w-4 h-4" /> Add card
          </button>
        )}
      </header>

      {/* Tab strip: Board / Archived */}
      <div
        className="inline-flex border border-[#e5e0d6] text-sm w-fit"
        data-testid="roadmap-tabs"
      >
        <button
          onClick={() => setTab("board")}
          className={`px-4 py-2 ${tab === "board" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
          data-testid="roadmap-tab-board"
        >
          Board
        </button>
        <button
          onClick={() => setTab("archived")}
          className={`px-4 py-2 border-l border-[#e5e0d6] ${tab === "archived" ? "bg-[#005e7a] text-white" : "bg-white text-[#4a4a52] hover:bg-[#f5efe6]"}`}
          data-testid="roadmap-tab-archived"
        >
          Archived
        </button>
      </div>

      {tab === "board" ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {board.columns.map((col) => {
              const cards = cardsInColumn(col.column_id);
              return (
                <DroppableColumn
                  key={col.column_id}
                  column={col}
                  cards={cards}
                  onEdit={setEditing}
                  onArchive={archive}
                  onDelete={del}
                  onTogglePublic={togglePublic}
                />
              );
            })}
          </div>
          <DragOverlay>
            {activeCard ? <Card card={activeCard} dragging /> : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <ArchivedList
          cards={archived}
          columns={board.columns}
          onRestore={restore}
          onDelete={del}
        />
      )}

      <NewCardDialog
        open={show}
        onOpenChange={setShow}
        columns={board.columns}
        defaultCol={draftCol}
        onSaved={() => { load(); setShow(false); }}
      />
      <EditCardDialog
        card={editing}
        columns={board.columns}
        onClose={() => setEditing(null)}
        onSaved={() => { load(); setEditing(null); }}
      />
    </div>
  );
}

// ────────────────────────────── COLUMNS ─────────────────────────────

function DroppableColumn({ column, cards, onEdit, onArchive, onDelete, onTogglePublic }) {
  // Stable id for the empty-column droppable (used when the user drags a
  // card into an empty column — there's no card to "hover over" so dnd-kit
  // needs the column itself to be droppable).
  const colDropId = `column-${column.column_id}`;
  return (
    <div
      className="bloq-card-soft p-3 sm:p-4 flex flex-col gap-3"
      data-testid={`column-${column.column_id}`}
    >
      <div className="flex items-center justify-between">
        <p className="bloq-headline text-base">{column.name}</p>
        <span className="text-xs text-[#82828c]">{cards.length}</span>
      </div>
      <SortableContext
        items={cards.length ? cards.map((c) => c.card_id) : [colDropId]}
        strategy={verticalListSortingStrategy}
        id={colDropId}
      >
        <div className="flex flex-col gap-2 min-h-[40px]" id={colDropId}>
          {cards.length === 0 ? (
            <EmptyDroppable id={colDropId} />
          ) : (
            cards.map((c) => (
              <SortableCard
                key={c.card_id}
                card={c}
                onEdit={onEdit}
                onArchive={onArchive}
                onDelete={onDelete}
                onTogglePublic={onTogglePublic}
              />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
}

function EmptyDroppable({ id }) {
  // dnd-kit needs SOME element to attach the column drop target to.
  // Render a thin dashed placeholder so the user has a visual hint that
  // they can drop a card here.
  const { setNodeRef } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      className="text-xs text-[#82828c] text-center py-6 border border-dashed border-[#e5e0d6] rounded"
      data-testid={`empty-${id}`}
    >
      Drop a card here
    </div>
  );
}

// ─────────────────────────────── CARDS ──────────────────────────────

function SortableCard(props) {
  const { card } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.card_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card {...props} dragHandleListeners={listeners} />
    </div>
  );
}

function Card({ card, onEdit, onArchive, onDelete, onTogglePublic, dragHandleListeners, dragging }) {
  return (
    <div
      className={`bloq-card p-3 group ${dragging ? "shadow-xl ring-2 ring-[#005e7a]/30" : ""}`}
      data-testid={`card-${card.card_id}`}
    >
      <div className="flex items-start gap-2">
        {/* Drag handle — only this area triggers dnd-kit's pointer
            sensor. Tapping the rest of the card opens the editor. */}
        <button
          type="button"
          className="touch-none cursor-grab active:cursor-grabbing text-[#82828c] hover:text-[#1a1a1f] p-1 -ml-1"
          aria-label="Drag to reorder"
          data-testid={`card-drag-${card.card_id}`}
          {...(dragHandleListeners || {})}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onEdit && onEdit(card)}
          className="flex-1 text-left min-w-0"
          data-testid={`card-edit-${card.card_id}`}
        >
          <p className="text-sm font-medium break-words text-[#005e7a]">{card.title}</p>
          {card.description && (
            <p className="text-xs text-[#4a4a52] mt-1 break-words line-clamp-3">{card.description}</p>
          )}
        </button>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <button
          onClick={() => onTogglePublic && onTogglePublic(card)}
          className={`px-1.5 py-0.5 text-[10px] inline-flex items-center gap-1 ${
            card.is_public ? "bg-[#005e7a] text-white" : "bg-[#f5efe6] text-[#82828c]"
          }`}
          title={card.is_public ? "Public — on /roadmap" : "Internal only"}
          data-testid={`card-public-${card.card_id}`}
        >
          {card.is_public ? <Globe className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
          {card.is_public ? "Public" : "Private"}
        </button>
        {card.target_quarter && (
          <span className="px-1.5 py-0.5 text-[10px] bg-[#f5efe6] text-[#4a4a52]">
            {card.target_quarter}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit && onEdit(card)}
          className="p-1 rounded hover:bg-[#f5efe6]"
          aria-label="Edit card"
          data-testid={`card-pencil-${card.card_id}`}
        >
          <Pencil className="w-3.5 h-3.5 text-[#82828c]" />
        </button>
        <button
          onClick={() => onArchive && onArchive(card)}
          className="p-1 rounded hover:bg-[#f5efe6]"
          aria-label="Archive card"
          data-testid={`card-archive-${card.card_id}`}
        >
          <Archive className="w-3.5 h-3.5 text-[#82828c]" />
        </button>
        <button
          onClick={() => onDelete && onDelete(card)}
          className="p-1 rounded hover:bg-[#f5efe6]"
          aria-label="Delete card"
          data-testid={`card-delete-${card.card_id}`}
        >
          <Trash2 className="w-3.5 h-3.5 text-[#82828c]" />
        </button>
      </div>
    </div>
  );
}

// ───────────────────────────── ARCHIVED LIST ─────────────────────────────

function ArchivedList({ cards, columns, onRestore, onDelete }) {
  const colName = (id) => columns.find((c) => c.column_id === id)?.name || "—";
  if (!cards.length) {
    return (
      <div className="bloq-card p-10 text-center" data-testid="archived-empty">
        <Archive className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
        <p className="bloq-headline text-lg mb-1 text-[#005e7a]">No archived cards</p>
        <p className="text-sm text-[#82828c]">When you archive a card, it lands here.</p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2" data-testid="archived-list">
      {cards.map((c) => (
        <div key={c.card_id} className="bloq-card p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium break-words text-[#005e7a]">{c.title}</p>
            {c.description && (
              <p className="text-xs text-[#4a4a52] mt-0.5 break-words line-clamp-2">{c.description}</p>
            )}
            <p className="text-[10px] uppercase tracking-widest text-[#82828c] mt-1">
              from {colName(c.column_id)}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => onRestore(c)}
              className="bloq-btn-secondary text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
              data-testid={`archived-restore-${c.card_id}`}
            >
              <ArchiveRestore className="w-3 h-3" /> Restore
            </button>
            <button
              onClick={() => onDelete(c)}
              className="text-xs px-3 py-1.5 text-[#c43838] hover:bg-[#c43838]/10"
              data-testid={`archived-delete-${c.card_id}`}
            >
              <Trash2 className="w-3 h-3 inline mr-1" /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────────── DIALOGS ─────────────────────────────

function NewCardDialog({ open, onOpenChange, columns, defaultCol, onSaved }) {
  const { currentWorkspace } = useWorkspace();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [col, setCol] = useState(defaultCol || "");
  useEffect(() => {
    setCol(defaultCol || columns[0]?.column_id || "");
    setTitle("");
    setDesc("");
  }, [open, defaultCol, columns]);

  const submit = async () => {
    if (!title.trim() || !col) {
      toast.error("Need a title and column");
      return;
    }
    await api.post(`/roadmap/cards`, {
      title: title.trim(),
      description: desc.trim(),
      column_id: col,
      workspace_id: currentWorkspace.workspace_id,
    });
    toast.success("Card added");
    onSaved();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New roadmap card</DialogTitle>
          <DialogDescription>
            Add an item to the roadmap. Drag it later to move between columns.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-2">
          <input
            className="bloq-input"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="new-card-title"
          />
          <textarea
            className="bloq-input"
            rows={3}
            placeholder="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            data-testid="new-card-desc"
          />
          <Select value={col} onValueChange={setCol}>
            <SelectTrigger data-testid="new-card-column">
              <SelectValue placeholder="Column" />
            </SelectTrigger>
            <SelectContent>
              {columns.map((c) => (
                <SelectItem key={c.column_id} value={c.column_id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button className="bloq-btn-primary" onClick={submit} data-testid="new-card-save">
            Add card
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditCardDialog({ card, columns, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [col, setCol] = useState("");
  const [quarter, setQuarter] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!card) return;
    setTitle(card.title || "");
    setDesc(card.description || "");
    setCol(card.column_id || "");
    setQuarter(card.target_quarter || "");
    setIsPublic(!!card.is_public);
  }, [card]);
  if (!card) return null;
  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/roadmap/cards/${card.card_id}`, {
        title: title.trim(),
        description: desc.trim(),
        column_id: col,
        target_quarter: quarter.trim(),
        is_public: isPublic,
      });
      toast.success("Card updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update card");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-card-dialog">
        <DialogHeader>
          <DialogTitle>Edit roadmap card</DialogTitle>
          <DialogDescription>
            Updates apply instantly. Toggle "Public" to surface this card on the
            marketing roadmap page.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-2">
          <input
            className="bloq-input"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="edit-card-title"
          />
          <textarea
            className="bloq-input"
            rows={4}
            placeholder="Description"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            data-testid="edit-card-desc"
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Column</label>
              <Select value={col} onValueChange={setCol}>
                <SelectTrigger className="mt-1" data-testid="edit-card-column">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((c) => (
                    <SelectItem key={c.column_id} value={c.column_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Target quarter</label>
              <input
                className="bloq-input mt-1"
                placeholder="Q2 2026"
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
                data-testid="edit-card-quarter"
              />
            </div>
          </div>
          <label className="flex items-center justify-between cursor-pointer py-2">
            <div>
              <p className="text-sm font-medium">Public on marketing roadmap</p>
              <p className="text-xs text-[#82828c]">
                When on, this card appears on /roadmap for everyone to see.
              </p>
            </div>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4 accent-[#005e7a]"
              data-testid="edit-card-public"
            />
          </label>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" disabled={busy}>Cancel</button>
          <button onClick={save} disabled={busy} className="bloq-btn-primary" data-testid="edit-card-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
